import React, { useEffect, useState, useMemo } from 'react';
import { supabase, supabase_get_templates, supabase_log_interaction, supabase_update_application_status, SUPABASE_CLIENT_FILTER_SELECT, supabase_get_vacancies, supabase_recruiter_publish_portal_proposal, supabase_recruiter_apply_voorgesteld_match_score } from '../services/supabaseService';
import { triggerAutomation } from '../services/automationService';
import { DEFAULT_KANBAN_STAGES } from '../constants';
import { formatPipelineInterviewHint, getClientPortalConsentBadgeLabel } from '../utils/clientPipelineConsent';
import { supabase_get_candidates_light, generateCandidateSlug } from '../services/supabaseService';
import CandidateMoveModal from './CandidateMoveModal';
import CandidateDetailModal from './CandidateDetailModal';
import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';
import BulkEmailComposerModal from './BulkEmailComposerModal';
import { DebugTag } from './DebugTag';
import { Loader2, Mail, Phone, ChevronRight, Search, Star, X, ChevronDown, Building2, TrendingUp, ArrowRight, Plus, Briefcase, Trash2 } from 'lucide-react';
import SearchableCombobox from './SearchableCombobox';
import GlobalVacancyLifecycleFilter from './GlobalVacancyLifecycleFilter';
import { loadUiState, saveUiState, uiStateKey } from '../services/uiState';
import { getCandidateEmail } from '../utils/candidateContact';
import { isPortalHideCandidateNameForStatus, isPortalBlockCandidateDetailForStatus, portalDisplayCandidateName, portalExcludeApplicationFromPipeline, portalPrivacyFromUser, isClientUuid, isPortalArchivedVacancy, vacancyLinkedToPortalOpdrachtgeverSet, filterPortalVisibleVacancies, sanitizeVacancyIdsForInFilter } from '../utils/clientPortalGuards';
import { VOORGESTELD_RESUME_BUNDLE_KEY, RESUME_VOORGESTELD_MOVE_MODAL_EVENT, resumeBundleMatchesVacancyEvent } from '../utils/voorgesteldResumeNavigation';
import { searchTextIncludes } from '../utils/searchText';
import {
    applicationMatchesClientFilters,
    buildEindklantFilterOptions,
    buildOpdrachtgeverFilterOptions,
    resolveVacancyForApplication,
    resolveEindklantDisplayName,
    resolveOpdrachtgeverDisplayName,
    effectiveOpdrachtgeverFilterId,
    clientFilterOptionSearchHaystack,
    filterVacanciesByStagesAndClient,
    formatClientFilterOptionLabel,
    parseLegacyVacancyFilterToLifecycleState,
    seedClientMapFromVacancyEmbeds,
    vacanciesFromApplications,
    type VacancyLifecycleStage,
    vacancyMatchesLifecycleStages,
    vacancyOpdrachtgeverId,
    vacancyEindklantId,
} from '../utils/vacancyClientFilters';

const CRM_SHORTLIST_ORG_SESSION_KEY = 'wt:crm_shortlist_org_client_id';
const CRM_SHORTLIST_STATUS_FOCUS_KEY = 'wt:crm_shortlist_status_focus';

function vacancyTouchesCrmOrgClient(v: any, clientId: string): boolean {
    if (!clientId || !v) return false;
    const d = v.data || {};
    const ids = [v.client_id, v.eindklant_id, v.opdrachtgever_id, d.eindklant_id, d.opdrachtgever_id, d.client_id]
        .map((x: any) => String(x || '').trim())
        .filter(Boolean);
    return ids.includes(clientId);
}

interface GlobalShortlistProps {
  onCandidateSelect?: (id: string) => void;
  onVacancySelect?: (id: string) => void;
  vacancies?: any[];
  isLoading?: boolean;
  user?: any;
  portalAllowedClientIds?: string[];
}

const STATUS_STYLE: Record<string, string> = {
  'New': 'bg-blue-50 text-blue-600 border-blue-100',
  'Op Shortlist': 'bg-amber-50 text-amber-700 border-amber-200',
  'Shortlist': 'bg-amber-50 text-amber-700 border-amber-200',
  'Voorgesteld': 'bg-violet-50 text-violet-700 border-violet-200',
  'Interview': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Gesprek': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Aangenomen': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Plaatsing': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Rejected': 'bg-rose-50 text-rose-600 border-rose-100',
};
const getStatusStyle = (s: string) =>
  STATUS_STYLE[s] || STATUS_STYLE[Object.keys(STATUS_STYLE).find(k => k.toLowerCase() === s?.toLowerCase()) || ''] || 'bg-slate-100 text-slate-500 border-slate-200';

const Avatar = ({ name, photo }: { name: string; photo?: string }) => (
  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center font-bold text-[11px] text-slate-500 border border-slate-200 shrink-0 overflow-hidden">
    {photo ? <img src={photo} className="w-full h-full object-cover" alt="" /> : name?.substring(0, 2).toUpperCase()}
  </div>
);

const GlobalShortlistTable: React.FC<GlobalShortlistProps> = ({ onCandidateSelect, onVacancySelect, vacancies: globalVacancies, isLoading = false, user, portalAllowedClientIds }) => {
  const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';
  const portalPrivacy = React.useMemo(() => portalPrivacyFromUser(user), [user?.portalFullPipelineVisibility]);
  const [vacancies, setVacancies] = useState<any[]>(globalVacancies || []);
  const [portalVacanciesLoading, setPortalVacanciesLoading] = useState(false);
  const defaultLifecycleStages: VacancyLifecycleStage[] = isClientPortal
    ? ['open', 'doing', 'gesloten']
    : ['open'];
  const [lifecycleStages, setLifecycleStages] =
    useState<VacancyLifecycleStage[]>(defaultLifecycleStages);
  const [scopeVacancyId, setScopeVacancyId] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [modalVacancyId, setModalVacancyId] = useState<string | undefined>(undefined);
  const [poolCandidates, setPoolCandidates] = useState<any[]>([]);
  const [candidatesSearch, setCandidatesSearch] = useState('');
  const [isSearchingBackend, setIsSearchingBackend] = useState(false);
  const searchTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [showBulkWhatsApp, setShowBulkWhatsApp] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
  const [targetAddVacancyId, setTargetAddVacancyId] = useState<string>('');
  const [targetAddStageId, setTargetAddStageId] = useState<string>(
    DEFAULT_KANBAN_STAGES.find((s) => s.name === 'Shortlist')?.id || DEFAULT_KANBAN_STAGES[0].id
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  const [apps, setApps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openVacancyCount, setOpenVacancyCount] = useState(0);
  const [clientMap, setClientMap] = useState<
    Record<string, { name: string; logo: string; bullhorn_id?: string | number | null; invoice_kvk?: string; data?: unknown }>
  >({});
  const [recruiterMap, setRecruiterMap] = useState<Record<string, string>>({});
  const [vacancyMap, setVacancyMap] = useState<Record<string, any>>({});

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpdrachtgever, setFilterOpdrachtgever] = useState('');
  const [filterEindklant, setFilterEindklant] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  /** Set when navigating from CRM company row → shortlist (sessionStorage). */
  const [crmOrgClientIdFilter, setCrmOrgClientIdFilter] = useState<string | null>(null);

  const [opdrachtgeverOpen, setOpdrachtgeverOpen] = useState(false);
  const [eindklantOpen, setEindklantOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [opdrachtgeverSearch, setOpdrachtgeverSearch] = useState('');
  const [eindklantSearch, setEindklantSearch] = useState('');
  const [isUpdatingMeta, setIsUpdatingMeta] = useState<Record<string, boolean>>({});

  // Persist filters across page switches
  const uiKey = useMemo(() => uiStateKey('globalShortlist:filters'), []);
  useEffect(() => {
    const saved = loadUiState(uiKey, null as any);
    if (!saved || typeof saved !== 'object') {
      /* defaults: open only (portal: all stages) */
    } else {
      if (Array.isArray(saved.lifecycleStages) && saved.lifecycleStages.length > 0) {
        setLifecycleStages(
          saved.lifecycleStages.filter((s: string) =>
            ['open', 'doing', 'gesloten'].includes(s)
          ) as VacancyLifecycleStage[]
        );
      } else if (typeof saved.vacancyFilter === 'string') {
        const migrated = parseLegacyVacancyFilterToLifecycleState(
          saved.vacancyFilter,
          isClientPortal
        );
        setLifecycleStages(migrated.stages);
        setScopeVacancyId(migrated.selectedVacancyId);
      }
      if (typeof saved.scopeVacancyId === 'string') setScopeVacancyId(saved.scopeVacancyId);
      else if (typeof saved.selectedVacancyId === 'string' && !saved.lifecycleStages) {
        setScopeVacancyId(saved.selectedVacancyId);
      }
      if (typeof saved.searchQuery === 'string') setSearchQuery(saved.searchQuery);
      if (typeof saved.filterOpdrachtgever === 'string') setFilterOpdrachtgever(saved.filterOpdrachtgever);
      if (typeof saved.filterEindklant === 'string') setFilterEindklant(saved.filterEindklant);
      if (Array.isArray(saved.filterStatuses)) setFilterStatuses(saved.filterStatuses.map(String));
    }

    try {
      const id = sessionStorage.getItem(CRM_SHORTLIST_ORG_SESSION_KEY);
      if (id && isClientUuid(id)) {
        sessionStorage.removeItem(CRM_SHORTLIST_ORG_SESSION_KEY);
        setCrmOrgClientIdFilter(id);
      }
      const statusFocus = sessionStorage.getItem(CRM_SHORTLIST_STATUS_FOCUS_KEY);
      if (statusFocus === '1') {
        sessionStorage.removeItem(CRM_SHORTLIST_STATUS_FOCUS_KEY);
        setFilterStatuses(['Shortlist', 'Op Shortlist']);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveUiState(uiKey, {
      lifecycleStages,
      scopeVacancyId,
      searchQuery,
      filterOpdrachtgever,
      filterEindklant,
      filterStatuses,
    });
  }, [uiKey, lifecycleStages, scopeVacancyId, searchQuery, filterOpdrachtgever, filterEindklant, filterStatuses]);

  const updateApplicationPipelineMeta = async (applicationId: string, patch: Record<string, any>) => {
    if (isClientPortal) return;
    if (!applicationId) return;
    setIsUpdatingMeta(p => ({ ...p, [applicationId]: true }));
    try {
      const { data: row, error } = await supabase.from('applications').select('cv_snapshot').eq('id', applicationId).single();
      if (error) throw error;
      const snap = (row?.cv_snapshot && typeof row.cv_snapshot === 'object') ? row.cv_snapshot : {};
      const next = {
        ...(snap || {}),
        pipeline_meta: {
          ...((snap as any)?.pipeline_meta || {}),
          ...patch
        }
      };
      const { error: upErr } = await supabase
        .from('applications')
        .update({ cv_snapshot: next, updated_at: new Date().toISOString() })
        .eq('id', applicationId);
      if (upErr) throw upErr;
      // Update local apps state to avoid full reload
      setApps(prev => prev.map(a => a.id === applicationId ? ({ ...a, cv_snapshot: next }) : a));
    } catch (e) {
      console.error('[GlobalShortlist] updateApplicationPipelineMeta failed', e);
    } finally {
      setIsUpdatingMeta(p => ({ ...p, [applicationId]: false }));
    }
  };

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveDetails, setMoveDetails] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [rejectionReasonsList, setRejectionReasonsList] = useState<string[]>(['Kandidaat teruggetrokken', 'Afgewezen door opdrachtgever', 'Opdracht ingetrokken', 'Overig']);

  useEffect(() => {
    const fn = (ev: Event) => {
      const d = (ev as CustomEvent<{ vacancyId?: string; applicationId?: string }>).detail;
      if (!d?.vacancyId || !modalVacancyId || d.vacancyId !== modalVacancyId) return;
      try {
        const raw = sessionStorage.getItem(VOORGESTELD_RESUME_BUNDLE_KEY);
        if (!raw) return;
        const b = JSON.parse(raw);
        if (!resumeBundleMatchesVacancyEvent(b, d)) return;
        if (!b.move) return;
        setMoveDetails(b.move);
        setShowMoveModal(true);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
    return () => window.removeEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
  }, [modalVacancyId]);

  const fetchVacancies = async () => {
    if (globalVacancies && globalVacancies.length > 0) {
      let list = Array.from(new Map(globalVacancies.map((v: any) => [v.id, v])).values());
      if (isClientPortal) {
        const ids = portalAllowedClientIds?.filter((x) => isClientUuid(x));
        if (ids?.length) {
          list = filterPortalVisibleVacancies(list.filter((v: any) => vacancyLinkedToPortalOpdrachtgeverSet(v, ids)));
          if (list.length === 0 && globalVacancies.length > 0) {
            list = Array.from(new Map(globalVacancies.map((v: any) => [v.id, v])).values());
          }
        }
      }
      setVacancies(list);
      return;
    }
    if (isClientPortal && isClientUuid(user?.clientId)) {
      setPortalVacanciesLoading(true);
      try {
        const data = await supabase_get_vacancies(user.clientId, undefined, true);
        if (data?.length) {
          setVacancies(Array.from(new Map(data.map((v: any) => [v.id, v])).values()));
          return;
        }
        setVacancies([]);
        return;
      } catch (e) {
        console.warn('[GlobalShortlist] portal supabase_get_vacancies failed', e);
        setVacancies([]);
        return;
      } finally {
        setPortalVacanciesLoading(false);
      }
    }
    if (isClientPortal) {
      setVacancies([]);
      return;
    }
    const { data, error } = await supabase.from('vacancies').select('id, title, status, data->lifecycleStatus as fallback_status, vacancy_number, data->location as location, created_at, data->isClosed as is_closed, data->werkplek as werkplek, data->clientName as client_name, data->publishedToSite as published_to_site, data->city as city, opdrachtgever_id, eindklant_id, client_id')
      .order('created_at', { ascending: false }).limit(10000);
    if (!error && data) {
      let formatted = data.map((v: any) => {
        // Mock data object
        v.data = {
            lifecycleStatus: v.fallback_status,
            location: v.location,
            isClosed: v.is_closed,
            werkplek: v.werkplek,
            clientName: v.client_name,
            publishedToSite: v.published_to_site,
            city: v.city
        };
        let status = v.fallback_status || v.status || v.data?.lifecycleStatus || 'Concept';
        const legacyStatus = status.toLowerCase();
        if (['draft', 'concept'].includes(legacyStatus)) status = 'Concept';
        else if (['doing', 'actief', 'interview', 'intake', 'sourcing', 'open'].includes(legacyStatus)) status = 'Open';
        else if (['afgerond', 'complete', 'succesvol', 'plaatsing', 'gesloten'].includes(legacyStatus)) status = 'Gesloten';
        else if (['done', 'afwezen', 'ingetrokken', 'archief', 'archive'].includes(legacyStatus) || v.data?.isClosed === true) status = 'Archief';

        return { ...v, status };
      });

      // We want to keep all vacancies for the dropdown if needed, but previously it was filtered.
      // If we use 'ALL' filter, we should probably allow all statuses in the dropdown too.
      setVacancies(formatted);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    fetchVacancies();
  }, [globalVacancies, isLoading, isClientPortal, user?.clientId, portalAllowedClientIds]);

  useEffect(() => {
    const sourceVacs = globalVacancies?.length ? globalVacancies : vacancies;
    if (!sourceVacs?.length) return;

    setClientMap((prev) => seedClientMapFromVacancyEmbeds(sourceVacs, prev));

    const ids = new Set<string>();
    sourceVacs.forEach((v: any) => {
      const o = effectiveOpdrachtgeverFilterId(v);
      const e = vacancyEindklantId(v);
      if (isClientUuid(o)) ids.add(o);
      if (isClientUuid(e)) ids.add(e);
    });
    const arr = Array.from(ids);
    if (arr.length === 0) return;
    let cancelled = false;
    (async () => {
      const merged = seedClientMapFromVacancyEmbeds(sourceVacs, {});
      const selectCols = SUPABASE_CLIENT_FILTER_SELECT;
      for (let i = 0; i < arr.length; i += 100) {
        const chunk = arr.slice(i, i + 100);
        let { data, error } = await supabase.from('clients').select(selectCols).in('id', chunk);
        if (error) {
          const fallback = await supabase
            .from('clients')
            .select('id, name, logo_url, invoice_kvk, data')
            .in('id', chunk);
          data = fallback.data;
          error = fallback.error;
        }
        if (error) {
          console.warn('[GlobalShortlist] clients chunk load failed', error);
          continue;
        }
        data?.forEach((c: any) => {
          merged[c.id] = {
            name: c.name,
            logo: c.logo_url,
            bullhorn_id: c.bullhorn_id ?? null,
            invoice_kvk: c.invoice_kvk,
            data: c.data,
          };
        });
      }
      if (!cancelled) setClientMap((prev) => ({ ...prev, ...merged }));
    })();
    return () => { cancelled = true; };
  }, [globalVacancies, vacancies]);

  useEffect(() => {
    if (!globalVacancies?.length) return;
    const vMap: Record<string, any> = {};
    globalVacancies.forEach((v: any) => {
      if (v?.id) vMap[v.id] = v;
    });
    setVacancyMap((prev) => ({ ...prev, ...vMap }));
    setClientMap((prev) => seedClientMapFromVacancyEmbeds(globalVacancies, prev));
  }, [globalVacancies]);

  useEffect(() => {
    // Fetch global automation templates as fallback, but client email templates take precedence
    supabase_get_templates().then(t => {
      setTemplates(prev => prev.length > 0 ? prev : (t || []));
    });
    supabase.auth.getUser().then(({ data: { user } }: any) => {
      if (user) {
        supabase.from('profiles').select('*').eq('id', user.id).single().then(({ data: profile }: any) => {
          setCurrentUser(profile ? { ...user, profile } : user);
          if (profile?.client_id) {
            supabase.from('clients').select('data').eq('id', profile.client_id).single().then(({ data: clientData }: any) => {
              if (clientData?.data?.ai_matching_settings?.rejectionReasons) {
                setRejectionReasonsList(clientData.data.ai_matching_settings.rejectionReasons);
              }
              if (clientData?.data?.email_templates) {
                setTemplates(clientData.data.email_templates);
              }
            });
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    if (isLoading) {
      setLoading(true);
      return;
    }
    const hasVacancyCatalog =
      (globalVacancies && globalVacancies.length > 0) ||
      vacancies.length > 0 ||
      isClientPortal;
    if (!hasVacancyCatalog) {
      setLoading(true);
      return;
    }
    const loadApps = async () => {
      setLoading(true);
      try {
        let allVacs =
          globalVacancies && globalVacancies.length > 0 ? globalVacancies : vacancies;
        if (!allVacs || allVacs.length === 0) {
          let vacancyQuery = supabase.from('vacancies').select('id, status, data->lifecycleStatus, data->isClosed, data->publishedToSite');
          const { data } = await vacancyQuery;
          allVacs = (data || []).map((v: any) => ({
              ...v,
              data: {
                  lifecycleStatus: v.lifecycleStatus,
                  isClosed: v.isClosed,
                  publishedToSite: v.publishedToSite
              }
          }));
        }

        if (!scopeVacancyId && lifecycleStages.length === 0) {
          setApps([]);
          setOpenVacancyCount(0);
          setLoading(false);
          return;
        }

        let scopedVacs: any[];
        if (scopeVacancyId) {
          const one = (allVacs || []).find((v: any) => v.id === scopeVacancyId);
          scopedVacs = one ? [one] : [];
        } else {
          scopedVacs = filterVacanciesByStagesAndClient(
            allVacs || [],
            lifecycleStages,
            {},
            { excludePortalArchived: isClientPortal }
          );
        }
        const filteredIds = scopedVacs.map((v: any) => v.id);

        setOpenVacancyCount(filteredIds.length);
        if (filteredIds.length === 0) { setApps([]); setLoading(false); return; }

        const targetIdsRaw = filteredIds;
        const targetIds = sanitizeVacancyIdsForInFilter(targetIdsRaw);
        if (!targetIds.length) { setApps([]); setLoading(false); return; }
        
        let allAppsData: any[] = [];
        
        if (targetIds && targetIds.length > 0) {
            const APP_CHUNK = 50;
            const chunks: string[][] = [];
            for (let i = 0; i < targetIds.length; i += APP_CHUNK) {
                chunks.push(targetIds.slice(i, i + APP_CHUNK));
            }
            
            // Process in batches of 5 concurrent requests to prevent browser/network overload
            for (let i = 0; i < chunks.length; i += 5) {
                const batch = chunks.slice(i, i + 5);
                const promises = batch.map(chunk => supabase
                    .from('applications')
                    .select('*, candidates(id, name, email, role, candidate_number, avatar_url, photo_url), vacancies(title), source_client:clients!source_client_id(name, logo_url)')
                    .in('vacancy_id', chunk)
                    // Allowing Closed and Rejected statuses
                    .order('created_at', { ascending: false }).limit(5000)
                );
                
                const results = await Promise.all(promises);
                for (const { data, error } of results) {
                    if (error) console.error('[GlobalShortlist] applications chunk error', error);
                    if (data) allAppsData.push(...data);
                }
            }
        }
        
        // Deduplicate to prevent React duplicate key warnings when pagination/chunking overlaps
        const uniqueApps = Array.from(new Map(allAppsData.map(a => [a.id, a])).values());
        const data = uniqueApps;
        const error = null;

        if (!error && data) {
          const vIds = Array.from(new Set(data.map((a: any) => a.vacancy_id).filter(Boolean)));
          const vData = (allVacs || []).filter((v: any) => vIds.includes(v.id));
          const vMap: Record<string, any> = {};
          const cIds = new Set<string>();
          const uIds = new Set<string>();
          vData.forEach((v: any) => {
            vMap[v.id] = v;
            const ogId = effectiveOpdrachtgeverFilterId(v);
            const ekId = vacancyEindklantId(v);
            if (isClientUuid(ogId)) cIds.add(ogId);
            if (isClientUuid(ekId)) cIds.add(ekId);
            const rId = v.assigned_recruiter_id || v.assignedRecruiterId;
            if (rId) uIds.add(rId);
          });
          if (Object.keys(vMap).length > 0) {
            setVacancyMap((prev) => ({ ...prev, ...vMap }));
          }

          const cMap: Record<string, any> = seedClientMapFromVacancyEmbeds(vData, {});
          const rMap: Record<string, string> = {};

          const fetchPromises: Promise<{ data: any[] | null }>[] = [];
          if (cIds.size > 0) {
            const cIdsArray = Array.from(cIds);
            for (let i = 0; i < cIdsArray.length; i += 100) {
              fetchPromises.push(
                supabase
                  .from('clients')
                  .select(SUPABASE_CLIENT_FILTER_SELECT)
                  .in('id', cIdsArray.slice(i, i + 100))
                  .then((res) => res)
              );
            }
          }
          if (uIds.size > 0) {
            const uIdsArray = Array.from(uIds);
            for (let i = 0; i < uIdsArray.length; i += 100) {
              fetchPromises.push(
                supabase
                  .from('profiles')
                  .select('id, name')
                  .in('id', uIdsArray.slice(i, i + 100))
                  .then((res) => res)
              );
            }
          }

          if (fetchPromises.length > 0) {
            const allResults = await Promise.all(fetchPromises);
            allResults.forEach(({ data: chunk }) => {
              if (chunk && chunk.length > 0) {
                if ('logo_url' in chunk[0]) {
                  chunk.forEach((c: any) => {
                    cMap[c.id] = {
                      name: c.name,
                      logo: c.logo_url,
                      bullhorn_id: c.bullhorn_id ?? null,
                      invoice_kvk: c.invoice_kvk,
                      data: c.data,
                    };
                  });
                } else if ('email' in chunk[0] || chunk[0].id) {
                  chunk.forEach((u: any) => {
                    if (u.name) rMap[u.id] = u.name;
                  });
                }
              }
            });
          }

          if (Object.keys(cMap).length > 0) setClientMap((prev) => ({ ...prev, ...cMap }));
          if (Object.keys(rMap).length > 0) setRecruiterMap((prev) => ({ ...prev, ...rMap }));
          setApps(data);
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    loadApps();
  }, [
    lifecycleStages,
    scopeVacancyId,
    refreshTrigger,
    globalVacancies,
    vacancies.length,
    isLoading,
    portalVacanciesLoading,
    isClientPortal,
  ]);

  const handleOpenModal = (app: any) => {
    if (isClientPortal && isPortalBlockCandidateDetailForStatus(app.status, app, portalPrivacy)) {
      window.dispatchEvent(
        new CustomEvent('wt:toast', {
          detail: {
            title: 'Niet beschikbaar',
            message: 'Kandidaten op shortlist open je hier niet.',
            type: 'error',
          },
        })
      );
      return;
    }
    const vac = vacancyMap[app.vacancy_id];
    const clientId = vac?.client_id || app.source_client?.id || '00000000-0000-0000-0000-000000000001';
    setSelectedCandidateId(app.candidate_id);
    setSelectedClientId(clientId);
    setModalVacancyId(app.vacancy_id);

    // Push URL slug
    const name = isPortalHideCandidateNameForStatus(app.status, app, portalPrivacy) ? 'kandidaat' : app.candidates?.name || 'kandidaat';
    const num = app.candidates?.candidate_number || app.candidate_id;
    const slug = generateCandidateSlug(name, num);
    const url = new URL(window.location.href);
    if (url.searchParams.get('candidate') !== slug) {
      url.searchParams.set('candidate', slug);
      window.history.pushState({}, '', url.pathname + url.search);
    }
  };

  const handleDeleteApplication = async (applicationId: string) => {
    if (isClientPortal) {
      window.dispatchEvent(
        new CustomEvent('wt:toast', {
          detail: { message: 'Verwijderen is niet beschikbaar in het klantportaal.', type: 'error' },
        })
      );
      return;
    }
    if (!confirm("Weet je zeker dat je deze kandidaat wilt verwijderen uit de shortlist/pipeline? Deze actie is onomkeerbaar.")) return;
    try {
      const { error } = await supabase.from('applications').delete().eq('id', applicationId);
      if (error) throw error;
      setApps(prev => prev.filter(a => a.id !== applicationId));
      setNotification({ message: 'Kandidaat succesvol verwijderd.', type: 'success' });
      setTimeout(() => setNotification(null), 4000);
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      console.error(e);
      setNotification({ message: 'Er is een fout opgetreden bij het verwijderen.', type: 'error' });
      setTimeout(() => setNotification(null), 4000);
    }
  };

  const handleOpenMoveModal = (app: any) => {
    if (isClientPortal) {
      window.dispatchEvent(
        new CustomEvent('wt:toast', {
          detail: { message: 'Status wijzigen is niet beschikbaar in het klantportaal.', type: 'error' },
        })
      );
      return;
    }
    const vac = vacancyMap[app.vacancy_id];
    const clientName = clientMap[vac?.client_id] ? clientMap[vac.client_id].name : app.source_client?.name || vac?.data?.werkplek || vac?.data?.clientName || '';
    const vd = vac?.data || app.vacancies?.data || {};
    const defaultClientEmail = String(
        vd.contactEmail || vd.clientContactEmail || vd.contactPersonEmail || vd.email || vd.hiringManagerEmail || ''
    ).trim();
    setMoveDetails({
      applicationId: app.id,
      candidateId: app.candidate_id,
      candidateRealId: app.candidate_id,
      vacancyId: app.vacancy_id,
      candidateName: app.candidates?.name || 'Kandidaat',
      candidateEmail: getCandidateEmail(app.candidates),
      fromStage: app.status || 'Shortlist',
      toStage: app.status || 'Shortlist',
      clientId: vac?.client_id || app.source_client?.id,
      vacancyTitle: vac?.title || app.vacancies?.title || '',
      clientName: clientName,
      defaultClientEmail: defaultClientEmail || undefined,
      matchScore: typeof app.match_score === 'number' ? app.match_score : undefined,
    });
    setShowMoveModal(true);
  };

  const handleConfirmMove = async (channel: 'email' | 'whatsapp' | 'log', templateId: string, subject: string, messageContent: string, internalNoteContent: string, targetStage: string, asDraft: boolean = false, rejectionReason?: string, cc?: string, bcc?: string, fromEmail?: string, voorgesteldClient?: import('../utils/portalProposalThread').VoorgesteldClientPayload | null, resolvedCandidateEmail?: string) => {
    if (isClientPortal) return;
    if (!moveDetails) return;
    try {
      const finalInternalNote = rejectionReason ? `${internalNoteContent}\n\nAfwijzingsreden: ${rejectionReason}`.trim() : internalNoteContent;
      const targetStageObj = DEFAULT_KANBAN_STAGES.find(s => s.name === targetStage || s.id === targetStage) || { id: targetStage, name: targetStage };
      await supabase_update_application_status(moveDetails.applicationId, targetStageObj.id);
      await triggerAutomation('stage_changed', { vacancyId: moveDetails.vacancyId, candidateId: moveDetails.candidateRealId, stageId: targetStageObj.id, stageName: targetStageObj.name, user_id: currentUser?.id });

      const isVoorgesteld = targetStageObj.name === 'Voorgesteld' || targetStageObj.id === 'Voorgesteld';
      if (isVoorgesteld && !asDraft) {
        const todayIso = new Date().toISOString().slice(0, 10);
        await updateApplicationPipelineMeta(moveDetails.applicationId, { voorgesteld_date: todayIso });
        if (voorgesteldClient && !voorgesteldClient.publishToPortal) {
          const { error: msErr } = await supabase_recruiter_apply_voorgesteld_match_score(
            moveDetails.applicationId,
            voorgesteldClient.portalMatchScore,
          );
          if (msErr) console.error('[GlobalShortlist] match_score sync', msErr);
        }
        if (voorgesteldClient?.publishToPortal) {
          const { error: pubErr } = await supabase_recruiter_publish_portal_proposal(
            moveDetails.applicationId,
            String(moveDetails.vacancyId || ''),
            voorgesteldClient,
            {
              recruiterName: currentUser?.profile?.name || currentUser?.email || 'Recruiter',
              clientId: moveDetails.clientId,
              candidateRealId: moveDetails.candidateRealId || moveDetails.candidateId,
            }
          );
          if (pubErr) console.error('[GlobalShortlist] portal publish', pubErr);
        }
        if (voorgesteldClient?.emailToClient && voorgesteldClient.clientEmailTo?.trim()) {
          try {
            const { sendDirectMessage } = await import('../services/microsoftGraphService');
            const clientEmailSignature = currentUser?.profile?.email_signature || '';
            let personalizedBody = voorgesteldClient.clientEmailBodyHtml.replace(/\n/g, '<br>');
            const isSignatureInBody =
              clientEmailSignature &&
              personalizedBody.replace(/\s/g, '').includes(clientEmailSignature.replace(/\s/g, '').substring(0, 20));
            if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
              personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, clientEmailSignature || '');
            } else if (clientEmailSignature && !isSignatureInBody) {
              personalizedBody += `<br><br>${clientEmailSignature}`;
            }
            const rawTo = voorgesteldClient.clientEmailTo.split(',').map((s) => s.trim()).filter(Boolean);
            const primary = rawTo[0];
            const ccFromPrimaryField = rawTo.slice(1);
            const ccFromPayload = (voorgesteldClient.clientEmailCc || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            const ccArr = [
              ...ccFromPrimaryField,
              ...ccFromPayload,
              ...(cc ? cc.split(',').map((s) => s.trim()).filter(Boolean) : []),
            ];
            const bccFromPayload = (voorgesteldClient.clientEmailBcc || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            const bccArr = [...bccFromPayload, ...(bcc ? bcc.split(',').map((s) => s.trim()).filter(Boolean) : [])];
            if (primary) {
              await sendDirectMessage(
                voorgesteldClient.clientEmailSubject,
                personalizedBody,
                primary,
                ccArr,
                bccArr,
                fromEmail || undefined
              );
            }
          } catch (e) {
            console.error('[GlobalShortlist] client email', e);
          }
        }
      }

      if (channel === 'email' && messageContent) {
        const clientEmailSignature = currentUser?.profile?.email_signature || '';
        try {
          let personalizedBody = messageContent.replace(/\n/g, '<br>');
          const isSignatureInBody = clientEmailSignature && personalizedBody.replace(/\s/g, '').includes(clientEmailSignature.replace(/\s/g, '').substring(0, 20));

          if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
            personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, clientEmailSignature || '');
          } else if (clientEmailSignature && !isSignatureInBody) {
            personalizedBody += `<br><br>${clientEmailSignature}`;
          }

          const { createDraftMessage, sendDirectMessage } = await import('../services/microsoftGraphService');
          const candEmail = (resolvedCandidateEmail || moveDetails.candidateEmail || '').trim();

          if (!candEmail) {
            alert("Geen e-mailadres gevonden voor deze kandidaat.");
            return;
          }

          const ccArr = cc ? cc.split(',').map(s => s.trim()).filter(Boolean) : [];
          const bccArr = bcc ? bcc.split(',').map(s => s.trim()).filter(Boolean) : [];

          if (asDraft) {
            const { success } = await createDraftMessage(subject, personalizedBody, candEmail, ccArr, bccArr, fromEmail || undefined);
            if (success) alert("Concept e-mail succesvol aangemaakt in Outlook!");
            else alert("Het aanmaken van een concept in Outlook is mislukt.");
          } else {
            const { success, error } = await sendDirectMessage(subject, personalizedBody, candEmail, ccArr, bccArr, fromEmail || undefined);
            if (success) alert("E-mail succesvol verzonden via Outlook!");
            else {
                if (error?.message?.includes('does not have the right to send mail')) {
                    alert(`Je hebt in Microsoft 365 geen "Verzenden Als" (Send As) rechten gekregen om te mailen vanuit de gedeelde mailbox: ${fromEmail}. Vraag je IT-beheerder om dit in te stellen of kies je eigen mailbox.`);
                } else {
                    alert("Het verzenden via Outlook is mislukt.");
                }
            }
          }
        } catch (e) {
          console.error("M365 Send Error", e);
          alert("Fout bij E-mail / M365 connectie");
        }
      } else if (channel === 'whatsapp' && messageContent) {
        if (asDraft) {
          await supabase.from('todos').insert({ title: `[OUTBOX WhatsApp] Bericht aan ${moveDetails.candidateName}`, description: messageContent, status: 'Pending', due_date: new Date().toISOString(), user_id: currentUser?.id, assignee_id: currentUser?.id });
          alert('WhatsApp bericht is opgeslagen in je Postvak UIT.');
        } else {
          const textUrl = encodeURIComponent(messageContent);
          window.open(`https://web.whatsapp.com/send?text=${textUrl}`, '_blank');
        }
      }

      if (moveDetails.clientId && currentUser) {
        const name = currentUser.profile?.name || 'Recruiter';
        await supabase_log_interaction(
          moveDetails.candidateRealId,
          moveDetails.clientId,
          'system',
          `(Status Update) ${name} heeft kandidaat verplaatst naar '${targetStage}'.${rejectionReason ? ` Reden: ${rejectionReason}` : ''}`
        );
        if (finalInternalNote && finalInternalNote.trim()) {
          await supabase_log_interaction(moveDetails.candidateRealId, moveDetails.clientId, 'note', `[Verplaatst naar ${targetStage}] ${finalInternalNote}`);
        }

        if (rejectionReason) {
          const { data: profile } = await supabase.from('candidates').select('ai_parsed_data').eq('id', moveDetails.candidateRealId).eq('client_id', moveDetails.clientId).single();
          if (profile) {
            const existingData = profile.ai_parsed_data || {};
            await supabase.from('candidates').update({
              ai_parsed_data: { ...existingData, rejectionReason: rejectionReason }
            }).eq('id', moveDetails.candidateRealId).eq('client_id', moveDetails.clientId);
          }
        }
      }
      setApps(apps.map(a => a.id === moveDetails.applicationId ? { ...a, status: targetStageObj.id } : a));
      setShowMoveModal(false); setMoveDetails(null);
    } catch (err) { console.error(err); alert('Er is iets misgegaan.'); }
  };

  const handleAddFromPool = async () => {
    if (isClientPortal) {
      window.dispatchEvent(
        new CustomEvent('wt:toast', {
          detail: { message: 'Kandidaten toevoegen is niet beschikbaar in het klantportaal.', type: 'error' },
        })
      );
      return;
    }
    if (!targetAddVacancyId || selectedPoolIds.length === 0) return;
    try {
      const inserts = selectedPoolIds.map(cid => ({
        vacancy_id: targetAddVacancyId,
        candidate_id: cid,
        status: targetAddStageId
      }));
      const { error } = await supabase.from('applications').insert(inserts);
      if (error) throw error;
      setNotification({ message: `Actie geslaagd (${selectedPoolIds.length} kandidaat/kandidaten toevevoegd)`, type: 'success' });
      setShowAddModal(false);
      setSelectedPoolIds([]);
      setRefreshTrigger(prev => prev + 1);
      setTimeout(() => setNotification(null), 4000);
    } catch (e) {
      console.error(e);
      setNotification({ message: 'Er is een fout opgetreden bij het toevoegen.', type: 'error' });
      setTimeout(() => setNotification(null), 4000);
    }
  };


  const getAppMeta = (app: any) => {
    const vac = resolveVacancyForApplication(app, vacancyMap, globalVacancies, vacancies);

    const op_id = effectiveOpdrachtgeverFilterId(vac);
    const opdrachtgeverName = resolveOpdrachtgeverDisplayName(vac, clientMap, app);
    const contractEmbed = vac ? (vac.clients ?? vac.client) : null;
    let opdrachtgeverLogo = op_id && clientMap[op_id] ? clientMap[op_id].logo : (vac?.opdrachtgever?.logo_url || null);
    if (!opdrachtgeverLogo) {
      opdrachtgeverLogo =
        contractEmbed?.logo_url ||
        app.source_client?.logo_url ||
        (vac?.client_id && clientMap[vac.client_id]?.logo) ||
        null;
    }
    const ek_id = vacancyEindklantId(vac);
    const eindklantName = resolveEindklantDisplayName(vac, clientMap);
    const eindklantLogo = ek_id && clientMap[ek_id] ? clientMap[ek_id].logo : (vac?.eindklant?.logo_url || null);

    const rId = vac ? (vac.assigned_recruiter_id || vac.assignedRecruiterId) : null;
    const recruiterName = rId && recruiterMap[rId] ? recruiterMap[rId] : (vac?.recruiter?.name || 'Systeem');
    return { opdrachtgeverName, opdrachtgeverLogo, eindklantName, eindklantLogo, recruiterName };
  };

  const filteredApps = useMemo(() => apps.filter(app => {
    const { opdrachtgeverName, eindklantName } = getAppMeta(app);
    const displayNameForSearch = portalDisplayCandidateName(app, isClientPortal, portalPrivacy);
    const candidateNameRaw = app.candidates?.name || '';
    const role = app.vacancies?.title || '';
    if (searchQuery) {
      const nameTokens = isClientPortal && isPortalHideCandidateNameForStatus(app.status, app, portalPrivacy)
        ? [displayNameForSearch]
        : [candidateNameRaw, displayNameForSearch];
      if (![...nameTokens, role, opdrachtgeverName, eindklantName].some((s) => searchTextIncludes(String(s), searchQuery))) {
        return false;
      }
    }
    if (
      !applicationMatchesClientFilters(
        app,
        { opdrachtgeverId: filterOpdrachtgever, eindklantId: filterEindklant },
        vacancyMap,
        globalVacancies,
        vacancies
      )
    )
      return false;
    if (filterStatuses.length > 0) {
      const st = String(app.status || 'New');
      const stLower = st.toLowerCase();
      const ok = filterStatuses.some((s) => {
        const t = String(s || '').trim();
        if (!t) return false;
        return st === t || stLower === t.toLowerCase();
      });
      if (!ok) return false;
    }

    if (isClientPortal) {
      const vac =
        vacancyMap[app.vacancy_id] ||
        globalVacancies?.find((v) => v.id === app.vacancy_id) ||
        vacancies.find((v) => v.id === app.vacancy_id);
      if (vac && isPortalArchivedVacancy(vac)) return false;
      const vacStatus = (vac?.status || 'Concept').toLowerCase();
      if (['concept', 'draft', 'afgewezen', 'ingetrokken'].includes(vacStatus)) return false;
      if (portalExcludeApplicationFromPipeline(app, portalPrivacy)) return false;
    }

    if (scopeVacancyId && app.vacancy_id !== scopeVacancyId) return false;
    if (!scopeVacancyId && lifecycleStages.length > 0) {
      const vac =
        vacancyMap[app.vacancy_id] ||
        globalVacancies?.find((v) => v.id === app.vacancy_id) ||
        vacancies.find((v) => v.id === app.vacancy_id);
      if (
        vac &&
        !vacancyMatchesLifecycleStages(vac, lifecycleStages, {
          excludePortalArchived: isClientPortal,
        })
      ) {
        return false;
      }
    }

    if (crmOrgClientIdFilter) {
      const vac =
        vacancyMap[app.vacancy_id] ||
        globalVacancies?.find((v) => v.id === app.vacancy_id) ||
        vacancies.find((v) => v.id === app.vacancy_id);
      if (!vacancyTouchesCrmOrgClient(vac, crmOrgClientIdFilter)) return false;
    }
    return true;
  }), [apps, searchQuery, filterOpdrachtgever, filterEindklant, filterStatuses, lifecycleStages, scopeVacancyId, vacancyMap, clientMap, isClientPortal, globalVacancies, vacancies, crmOrgClientIdFilter, portalPrivacy]);

  /** Alleen vacatures/klanten uit de geladen shortlist (niet alle vacatures in scope). */
  const vacanciesInShortlistList = useMemo(
    () => vacanciesFromApplications(apps, vacancyMap, globalVacancies, vacancies),
    [apps, vacancyMap, globalVacancies, vacancies]
  );

  const uniqueOpdrachtgevers = useMemo(
    () =>
      buildOpdrachtgeverFilterOptions(
        vacanciesInShortlistList,
        clientMap,
        apps,
        vacancyMap,
        globalVacancies,
        vacancies
      ),
    [vacanciesInShortlistList, clientMap, apps, vacancyMap, globalVacancies, vacancies]
  );

  const uniqueEindklants = useMemo(
    () =>
      buildEindklantFilterOptions(
        vacanciesInShortlistList,
        clientMap,
        apps,
        vacancyMap,
        globalVacancies,
        vacancies
      ),
    [vacanciesInShortlistList, clientMap, apps, vacancyMap, globalVacancies, vacancies]
  );

  const applicationCountByVacancyId = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of apps) {
      const vid = String(a?.vacancy_id || '').trim();
      if (vid) m[vid] = (m[vid] || 0) + 1;
    }
    return m;
  }, [apps]);

  const uniqueStatuses = useMemo(() => Array.from(new Set(apps.map(app => app.status || 'New'))).sort(), [apps]);
  const hasFilters = searchQuery || filterOpdrachtgever || filterEindklant || filterStatuses.length > 0 || !!crmOrgClientIdFilter;
  const filteredOpdrachtgevers = uniqueOpdrachtgevers.filter(
    (o) => !opdrachtgeverSearch || searchTextIncludes(clientFilterOptionSearchHaystack(o), opdrachtgeverSearch)
  );
  const filteredEindklants = uniqueEindklants.filter(
    (o) => !eindklantSearch || searchTextIncludes(clientFilterOptionSearchHaystack(o), eindklantSearch)
  );

  const selectedOgFilter = filterOpdrachtgever ? uniqueOpdrachtgevers.find(x => x.id === filterOpdrachtgever) : undefined;
  const selectedEkFilter = filterEindklant ? uniqueEindklants.find(x => x.id === filterEindklant) : undefined;

  useEffect(() => {
    if (filterOpdrachtgever && !uniqueOpdrachtgevers.some((o) => o.id === filterOpdrachtgever)) {
      setFilterOpdrachtgever('');
    }
  }, [filterOpdrachtgever, uniqueOpdrachtgevers]);

  useEffect(() => {
    if (filterEindklant && !uniqueEindklants.some((o) => o.id === filterEindklant)) {
      setFilterEindklant('');
    }
  }, [filterEindklant, uniqueEindklants]);

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
              <Star size={15} className="text-amber-500" />
            </div>
            <div>
              <h1 className="text-[15px] font-bold text-slate-900">Shortlist</h1>
              <p className="text-[11px] text-slate-400 font-medium">{filteredApps.length} kandidaten · <span className="text-slate-500">{openVacancyCount} open vacatures</span></p>
              {crmOrgClientIdFilter && (
                <p className="text-[10px] font-bold text-indigo-700 mt-1">
                  Filter vanuit CRM: vacatures gekoppeld aan{' '}
                  <span className="underline">{clientMap[crmOrgClientIdFilter]?.name || 'organisatie'}</span>
                  {' '}— tik op <span className="font-black">Wissen</span> om breed te tonen.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasFilters && (
              <button onClick={() => { setSearchQuery(''); setFilterOpdrachtgever(''); setFilterEindklant(''); setFilterStatuses([]); setSelectedAppIds([]); setCrmOrgClientIdFilter(null); }} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">
                <X size={11} /> Wissen
              </button>
            )}
            {!isClientPortal && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm flex items-center gap-1.5"
            >
              <Plus size={14} /> Kandidaat toevoegen
            </button>
            )}
            {selectedAppIds.length > 0 && (
              <div className="flex items-center gap-2 border-l border-slate-200 pl-3 ml-1">
                 {isClientPortal ? (
                     <>
                        <button onClick={() => alert('Upgrade portal voor mail and whatsapp, contact werfplaats')} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#25D366]/50 rounded-xl flex items-center gap-1.5 blur-[2px] cursor-not-allowed" title="Upgrade portal voor mail and whatsapp, contact werfplaats">
                            <Phone size={14} /> Appen
                        </button>
                        <button onClick={() => alert('Upgrade portal voor mail and whatsapp, contact werfplaats')} className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white/50 border border-slate-200 rounded-xl flex items-center gap-1.5 blur-[2px] cursor-not-allowed" title="Upgrade portal voor mail and whatsapp, contact werfplaats">
                            <Mail size={14} /> Mailen
                        </button>
                     </>
                 ) : (
                     <>
                        <button onClick={() => setShowBulkWhatsApp(true)} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#25D366] hover:bg-[#128C7E] rounded-xl transition-colors shadow-sm flex items-center gap-1.5">
                            <Phone size={14} /> {selectedAppIds.length > 1 ? 'Samen Appen' : 'Appen'} ({selectedAppIds.length})
                        </button>
                        <button onClick={() => setShowBulkEmail(true)} className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors shadow-sm flex items-center gap-1.5">
                            <Mail size={14} /> {selectedAppIds.length > 1 ? 'Samen Mailen' : 'Mailen'} ({selectedAppIds.length})
                        </button>
                     </>
                 )}
              </div>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className={`w-full grid grid-cols-1 sm:grid-cols-2 ${isClientPortal ? 'xl:grid-cols-4' : 'xl:grid-cols-5'} gap-2 relative items-stretch`}>
          <GlobalVacancyLifecycleFilter
            vacancies={globalVacancies && globalVacancies.length > 0 ? globalVacancies : vacancies}
            lifecycleStages={lifecycleStages}
            onLifecycleStagesChange={setLifecycleStages}
            selectedVacancyId={scopeVacancyId}
            onSelectedVacancyIdChange={setScopeVacancyId}
            clientMap={clientMap}
            applicationCountByVacancyId={applicationCountByVacancyId}
            isClientPortal={isClientPortal}
          />

          <div className="relative min-w-0 w-full">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoek kandidaat of vacature..." className="w-full pl-8 pr-3 py-1.5 text-[12px] bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:bg-white transition-colors font-medium" />
          </div>

          {/* Opdrachtgever — verborgen in het client portal: de ingelogde organisatie ÍS de opdrachtgever. */}
          {!isClientPortal && (
          <div className="relative min-w-0 w-full">
            <button onClick={() => { setOpdrachtgeverOpen(!opdrachtgeverOpen); setEindklantOpen(false); setStatusOpen(false); }} className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${filterOpdrachtgever ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              <Building2 size={11} className="shrink-0" />
              <span className="min-w-0 flex-1 text-left truncate">{selectedOgFilter ? formatClientFilterOptionLabel(selectedOgFilter) : 'Opdrachtgever'}</span>
              {filterOpdrachtgever ? <X size={10} onClick={e => { e.stopPropagation(); setFilterOpdrachtgever(''); }} className="text-emerald-400 shrink-0" /> : <ChevronDown size={10} className="shrink-0" />}
            </button>
            {opdrachtgeverOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                <div className="p-2 border-b border-slate-100"><input autoFocus value={opdrachtgeverSearch} onChange={e => setOpdrachtgeverSearch(e.target.value)} placeholder="Zoeken..." className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none" /></div>
                <div className="max-h-48 overflow-y-auto">
                  <button onClick={() => { setFilterOpdrachtgever(''); setOpdrachtgeverOpen(false); setOpdrachtgeverSearch(''); }} className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50">Alle opdrachtgevers</button>
                  {filteredOpdrachtgevers.map(o => (
                    <button
                      key={o.id}
                      onClick={() => { setFilterOpdrachtgever(o.id); setOpdrachtgeverOpen(false); setOpdrachtgeverSearch(''); }}
                      className={`w-full text-left px-3 py-2 text-[11px] hover:bg-emerald-50 transition-colors ${filterOpdrachtgever === o.id ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-700'}`}
                    >
                      <div className="min-w-0 font-medium truncate">
                        {o.companyNumber ? (
                          <span className="font-mono text-[10px] text-slate-500 mr-1.5">{o.companyNumber}</span>
                        ) : null}
                        {o.companyName}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Eindklant */}
          <div className="relative min-w-0 w-full">
            <button onClick={() => { setEindklantOpen(!eindklantOpen); setOpdrachtgeverOpen(false); setStatusOpen(false); }} className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${filterEindklant ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              <Building2 size={11} className="shrink-0" />
              <span className="min-w-0 flex-1 text-left truncate">{selectedEkFilter ? formatClientFilterOptionLabel(selectedEkFilter) : 'Werkplek'}</span>
              {filterEindklant ? <X size={10} onClick={e => { e.stopPropagation(); setFilterEindklant(''); }} className="text-indigo-400 shrink-0" /> : <ChevronDown size={10} className="shrink-0" />}
            </button>
            {eindklantOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                <div className="p-2 border-b border-slate-100">
                  <input autoFocus value={eindklantSearch} onChange={e => setEindklantSearch(e.target.value)} placeholder="Zoeken..." className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none" />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  <button onClick={() => { setFilterEindklant(''); setEindklantOpen(false); setEindklantSearch(''); }} className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50">Alle werkplekken</button>
                  {filteredEindklants.map(o => (
                      <button
                        key={o.id}
                        onClick={() => { setFilterEindklant(o.id); setEindklantOpen(false); setEindklantSearch(''); }}
                        className={`w-full text-left px-3 py-2 text-[11px] hover:bg-indigo-50 transition-colors ${filterEindklant === o.id ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-700'}`}
                      >
                        <div className="min-w-0 font-medium truncate">
                          {o.companyNumber ? (
                            <span className="font-mono text-[10px] text-slate-500 mr-1.5">{o.companyNumber}</span>
                          ) : null}
                          {o.companyName}
                        </div>
                      </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Status (multi-select) */}
          <div className="relative min-w-0 w-full">
            <button onClick={() => { setStatusOpen(!statusOpen); setOpdrachtgeverOpen(false); setEindklantOpen(false); }} className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${filterStatuses.length > 0 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              <TrendingUp size={11} className="shrink-0" />
              <span className="min-w-0 flex-1 text-left truncate">{filterStatuses.length > 0 ? `${filterStatuses.length} status` : 'Status'}</span>
              {filterStatuses.length > 0 ? <X size={10} onClick={e => { e.stopPropagation(); setFilterStatuses([]); }} className="text-amber-400 shrink-0" /> : <ChevronDown size={10} className="shrink-0" />}
            </button>
            {statusOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                <div className="max-h-48 overflow-y-auto">
                  <button onClick={() => { setFilterStatuses([]); }} className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50">Alle statussen</button>
                  {uniqueStatuses.map(s => {
                    const checked = filterStatuses.includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => setFilterStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                        className={`w-full text-left px-3 py-2 text-[11px] hover:bg-amber-50 transition-colors ${checked ? 'bg-amber-50 text-amber-700 font-semibold' : 'text-slate-700'}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <input type="checkbox" readOnly checked={checked} className="rounded border-slate-300 text-amber-600" />
                          <span>{s}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(opdrachtgeverOpen || eindklantOpen || statusOpen) && <div className="fixed inset-0 z-40" onClick={() => { setOpdrachtgeverOpen(false); setEindklantOpen(false); setStatusOpen(false); }} />}

      {/* Table */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white">
        {loading || portalVacanciesLoading ? (
          <div className="flex-1 flex items-center justify-center"><div className="flex flex-col items-center gap-2"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /><p className="text-[11px] text-slate-400 font-medium">Shortlist laden...</p></div></div>
        ) : filteredApps.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <Star size={28} className="mb-2 opacity-20" />
            <p className="text-sm font-medium">Geen kandidaten gevonden</p>
            {hasFilters && <button onClick={() => { setSearchQuery(''); setFilterOpdrachtgever(''); setFilterEindklant(''); setFilterStatuses([]); setSelectedAppIds([]); }} className="mt-2 text-[12px] text-indigo-500 hover:underline font-medium">Filters wissen</button>}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto bg-white">
            <table className="w-full text-left border-collapse">
              <thead className="bg-white sticky top-0 z-10 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3 w-10">
                    {!isClientPortal ? (
                    <input 
                      type="checkbox"
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      checked={filteredApps.length > 0 && selectedAppIds.length === filteredApps.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedAppIds(filteredApps.map(a => a.id));
                        else setSelectedAppIds([]);
                      }}
                    />
                    ) : null}
                  </th>
                  <th className="px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Kandidaat</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest hidden md:table-cell">Opdrachtgever</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest hidden lg:table-cell">Werkplek</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Vacature</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest hidden xl:table-cell">Recruiter</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest hidden lg:table-cell">Update</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map(app => {
                  const { opdrachtgeverName, opdrachtgeverLogo, eindklantName, eindklantLogo, recruiterName } = getAppMeta(app);
                  const candidateName = portalDisplayCandidateName(app, isClientPortal, portalPrivacy);
                  const hideIdentity = isClientPortal && isPortalHideCandidateNameForStatus(app.status, app, portalPrivacy);
                  const blockDetail = isClientPortal && isPortalBlockCandidateDetailForStatus(app.status, app, portalPrivacy);
                  const status = app.status || 'New';
                  const clientConsentBadge = getClientPortalConsentBadgeLabel(app.status);
                  const interviewSlotHint = formatPipelineInterviewHint(
                      app.cv_snapshot?.pipeline_meta as Record<string, unknown> | undefined,
                  );
                  const lastUpdate = new Date(app.updated_at || app.created_at);
                  const diffDays = Math.floor((Date.now() - lastUpdate.getTime()) / 86400000);
                  const updateLabel = diffDays === 0 ? 'Vandaag' : diffDays === 1 ? 'Gisteren' : `${diffDays}d`;

                  return (
                    <tr key={app.id}
                      onClick={() => { if (onVacancySelect && app.vacancy_id) onVacancySelect(app.vacancy_id); }}
                      className="bg-white border-b border-slate-100 hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    >
                      {/* Kandidaat — opens CandidateDetailModal */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {!isClientPortal ? (
                        <input 
                          type="checkbox"
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          checked={selectedAppIds.includes(app.id)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedAppIds([...selectedAppIds, app.id]);
                            else setSelectedAppIds(selectedAppIds.filter(id => id !== app.id));
                          }}
                        />
                        ) : null}
                      </td>
                      <td className="px-5 py-3" onClick={e => { e.stopPropagation(); if (!blockDetail) handleOpenModal(app); }}>
                        <div className={`flex items-center gap-2.5 ${blockDetail ? 'cursor-default' : 'cursor-pointer'}`}>
                          <Avatar name={candidateName} photo={hideIdentity ? undefined : (app.candidates?.photo_url || app.candidates?.avatar_url)} />
                          <div className="min-w-0">
                            <p className={`text-[13px] font-semibold text-slate-800 leading-tight truncate ${blockDetail ? '' : 'group-hover:text-[#E21E26] transition-colors'}`}>{candidateName}</p>
                            <p className="text-[10px] text-slate-400 font-medium mt-0.5">{new Date(app.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}</p>
                            {interviewSlotHint ? (
                              <p className="text-[10px] font-semibold text-violet-700 mt-0.5 truncate" title={interviewSlotHint}>
                                {interviewSlotHint}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      {/* Opdrachtgever */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center gap-1.5">
                          {opdrachtgeverLogo ? <img src={opdrachtgeverLogo} alt="" className="w-5 h-5 object-contain rounded-md bg-white border border-slate-100 shrink-0" /> : <div className="w-5 h-5 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-400 shrink-0">{opdrachtgeverName.charAt(0)}</div>}
                          <span className="text-[12px] font-medium text-slate-700 truncate max-w-[140px]">{opdrachtgeverName}</span>
                        </div>
                      </td>

                      {/* Eindklant */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {eindklantName !== '-' ? (
                          <div className="flex items-center gap-1.5">
                            {eindklantLogo ? <img src={eindklantLogo} alt="" className="w-5 h-5 object-contain rounded-md bg-white border border-slate-100 shrink-0" /> : <div className="w-5 h-5 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-400 shrink-0">{eindklantName.charAt(0)}</div>}
                            <span className="text-[12px] font-medium text-slate-600 truncate max-w-[120px]">{eindklantName}</span>
                          </div>
                        ) : <span className="text-slate-200 text-[11px]">—</span>}
                      </td>

                      {/* Vacature — clickable to go to vacancy */}
                      <td className="px-4 py-3">
                        <button onClick={e => { e.stopPropagation(); if (onVacancySelect && app.vacancy_id) onVacancySelect(app.vacancy_id); }} className="text-[12px] font-medium text-slate-700 hover:text-[#e51b23] truncate max-w-[160px] block text-left transition-colors flex items-center gap-1 group/vac">
                          {app.vacancies?.title || 'Onbekend'}
                          <ArrowRight size={10} className="opacity-0 group-hover/vac:opacity-100 transition-opacity shrink-0" />
                        </button>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        {isClientPortal ? (
                          <div className="flex flex-col items-start gap-1.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusStyle(status)}`}>
                            {status}
                          </span>
                          {clientConsentBadge ? (
                            <span className="inline-flex max-w-[200px] items-center rounded-md bg-sky-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-800 ring-1 ring-sky-100">
                              {clientConsentBadge}
                            </span>
                          ) : null}
                          </div>
                        ) : (
                        <div className="flex flex-col items-start gap-1.5">
                        <button onClick={e => { e.stopPropagation(); handleOpenMoveModal(app); }} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold border hover:opacity-80 transition-opacity ${getStatusStyle(status)}`}>
                          {status}
                        </button>
                        {clientConsentBadge ? (
                          <span className="inline-flex max-w-[200px] items-center rounded-md bg-sky-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-800 ring-1 ring-sky-100" title={clientConsentBadge}>
                            {clientConsentBadge}
                          </span>
                        ) : null}
                        </div>
                        )}
                        {!isClientPortal && (status === 'Gesprek' || status === 'Voorgesteld') && (
                          <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()}>
                            {status === 'Gesprek' && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Gesprek</span>
                                <select
                                  value={app.cv_snapshot?.pipeline_meta?.gesprek_round || '1'}
                                  onChange={(e) => updateApplicationPipelineMeta(app.id, { gesprek_round: e.target.value })}
                                  className="text-[9px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none"
                                  title="Gespreksronde"
                                >
                                  <option value="1">1e</option>
                                  <option value="2">2e</option>
                                  <option value="3">3e</option>
                                </select>
                                <input
                                  type="date"
                                  value={(app.cv_snapshot?.pipeline_meta?.gesprek_date || '').slice(0, 10)}
                                  onChange={(e) => updateApplicationPipelineMeta(app.id, { gesprek_date: e.target.value })}
                                  className="text-[9px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none"
                                  title="Gespreksdatum"
                                />
                                {isUpdatingMeta[app.id] && <Loader2 size={12} className="animate-spin text-slate-400" />}
                              </div>
                            )}
                            {status === 'Voorgesteld' && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Voorgesteld</span>
                                <input
                                  type="date"
                                  value={(app.cv_snapshot?.pipeline_meta?.voorgesteld_date || '').slice(0, 10)}
                                  onChange={(e) => updateApplicationPipelineMeta(app.id, { voorgesteld_date: e.target.value })}
                                  className="text-[9px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none"
                                  title="Datum voorgesteld"
                                />
                                {isUpdatingMeta[app.id] && <Loader2 size={12} className="animate-spin text-slate-400" />}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Recruiter */}
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-400 shrink-0">{recruiterName.charAt(0)}</div>
                          <span className="text-[11px] font-medium text-slate-500 truncate max-w-[100px]">{recruiterName}</span>
                        </div>
                      </td>

                      {/* Update */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`text-[11px] font-semibold ${diffDays <= 1 ? 'text-emerald-600' : diffDays > 30 ? 'text-slate-300' : 'text-slate-500'}`}>{updateLabel}</span>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-slate-300">
                          {!hideIdentity && app.candidates?.email && <a href={`mailto:${app.candidates.email}`} onClick={e => e.stopPropagation()} className="p-1 hover:text-blue-500 hover:bg-blue-50 rounded-md transition-colors"><Mail size={12} /></a>}
                          {!hideIdentity && app.candidates?.phone && <a href={`tel:${app.candidates.phone}`} onClick={e => e.stopPropagation()} className="p-1 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors"><Phone size={12} /></a>}
                        </div>
                      </td>

                      {/* Open modal arrow / Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 opacity-100 group-hover:opacity-100 transition-opacity">
                          {!isClientPortal && (
                          <button onClick={e => { e.stopPropagation(); handleDeleteApplication(app.id); }} className="p-1.5 text-slate-300 hover:text-[#E21E26] hover:bg-red-50 rounded-lg transition-colors" title="Verwijder uit Pipeline">
                            <Trash2 size={13} />
                          </button>
                          )}
                          {!blockDetail && (
                          <button onClick={e => { e.stopPropagation(); handleOpenModal(app); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                            <ChevronRight size={14} />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && filteredApps.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-2.5 bg-white shrink-0 flex items-center justify-between">
          <span className="text-[11px] font-medium text-slate-400">{filteredApps.length} kandidaten · {openVacancyCount} open vacatures</span>
          {hasFilters && <span className="text-[11px] text-indigo-500 font-medium">Gefilterd</span>}
        </div>
      )}

      <CandidateMoveModal
        isOpen={showMoveModal}
        onClose={() => { setShowMoveModal(false); setMoveDetails(null); }}
        moveDetails={moveDetails}
        templates={templates}
        currentUser={currentUser}
        rejectionReasons={rejectionReasonsList}
        onConfirm={handleConfirmMove}
      />

      {/* Candidate Detail Modal */}
      {selectedCandidateId && (
        <CandidateDetailModal
          candidateId={selectedCandidateId}
          clientId={selectedClientId}
          vacancyId={modalVacancyId}
          currentUser={isClientPortal && user ? { ...currentUser, ...user } : currentUser}
          forceReadOnly={isClientPortal}
          stages={DEFAULT_KANBAN_STAGES}
          onUpdate={() => { }}
          onClose={() => {
            setSelectedCandidateId(null);
            setSelectedClientId('');
            setModalVacancyId(undefined);
            const url = new URL(window.location.href);
            if (url.searchParams.has('candidate')) {
              url.searchParams.delete('candidate');
              window.history.pushState({}, '', url.pathname + url.search);
            }
          }}
        />
      )}

      {showBulkWhatsApp && (
        <BulkWhatsAppComposerModal
          isOpen={showBulkWhatsApp}
          onClose={() => setShowBulkWhatsApp(false)}
          selectedCandidates={apps.filter(a => selectedAppIds.includes(a.id)).map(a => ({
             id: a.candidate_id,
             name: a.candidates?.name || '',
             email: a.candidates?.email || '',
             phone: a.candidates?.phone || '',
             profile_id: a.candidate_id
          }))}
          onSuccessCallback={() => { setSelectedAppIds([]); setShowBulkWhatsApp(false); }}
        />
      )}
      {showBulkEmail && (
        <BulkEmailComposerModal
          isOpen={showBulkEmail}
          onClose={() => setShowBulkEmail(false)}
          selectedCandidates={apps
            .filter(a => selectedAppIds.includes(a.id))
            .map(a => {
              const vac = vacancyMap[a.vacancy_id];
              const companyName =
                clientMap[vac?.client_id] ? clientMap[vac.client_id].name :
                a.source_client?.name ||
                vac?.data?.werkplek ||
                vac?.data?.clientName ||
                '';
              const vacancyTitle = vac?.title || a.vacancies?.title || '';
              return {
                id: a.candidate_id,
                name: a.candidates?.name || '',
                email: a.candidates?.email || '',
                phone: a.candidates?.phone || '',
                profile_id: a.candidate_id,
                companyName,
                vacancyTitle,
                role: a.candidates?.role || '',
              };
            })}
          onSuccessCallback={() => { setSelectedAppIds([]); setShowBulkEmail(false); }}
        />
      )}

      {/* Add from Talent Pool Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[200] flex justify-end bg-slate-900/60 backdrop-blur-sm transition-opacity animate-in fade-in">
          <div className="bg-white w-full max-w-[550px] shadow-2xl h-full flex flex-col animate-slideInRight duration-300">
            {/* Header */}
            <div className="p-5 sm:px-6 sm:py-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Kandidaat Toevoegen</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 sm:p-6 bg-[#f8fafc] space-y-5">

              {/* Box 1: Select Vacancy */}
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-[10px] font-black text-red-600 uppercase tracking-widest">1. Kies Vacature</label>
                </div>
                <SearchableCombobox
                  options={vacancies.map(v => ({
                    id: v.id,
                    title: v.title,
                    subtitle: v.data?.location || 'Geen locatie',
                    badge: v.vacancy_number || undefined
                  }))}
                  value={targetAddVacancyId}
                  onChange={(val) => setTargetAddVacancyId(val)}
                  placeholder="Zoek en selecteer vacature..."
                  searchPlaceholder="Zoek op vacaturenaam, nummer of werkplek..."
                  icon={Briefcase}
                />
              </div>

              {/* Box 2: Select Candidates */}
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex flex-col min-h-[300px]">
                <label className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-3">2. Kies Kandidaten (Talentpool)</label>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Zoek candidaten op naam, rol etc..."
                    value={candidatesSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCandidatesSearch(val);
                      if (searchTimeout.current) clearTimeout(searchTimeout.current);
                      if (val.trim().length >= 2) {
                        setIsSearchingBackend(true);
                        searchTimeout.current = setTimeout(async () => {
                          try {
                            const { supabase_get_paginated_talent_pool } = await import('../services/supabaseService');
                            const res = await supabase_get_paginated_talent_pool(undefined, 1, 50, val);
                            if (res && res.data) {
                              const extracted = res.data.map((tp: any) => ({
                                ...tp.candidate,
                                candidate_id: tp.candidate_id || tp.candidate?.id || tp.id,
                                ai_parsed_data: tp.ai_parsed_data || tp.candidate?.ai_parsed_data
                              })).filter(Boolean);
                              const unique = Array.from(new Map(extracted.map((c: any) => [c.candidate_id, c])).values());
                              setPoolCandidates(unique);
                            } else {
                              setPoolCandidates([]);
                            }
                          } catch (e) { } finally { setIsSearchingBackend(false); }
                        }, 400);
                      } else {
                        setPoolCandidates([]);
                      }
                    }}
                    className="w-full text-xs font-bold bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#0073e6]/20 focus:border-[#0073e6] outline-none pl-9 pr-4 py-3 placeholder:font-medium placeholder:text-slate-400 transition-colors"
                  />
                  {isSearchingBackend && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                </div>

                <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl overflow-hidden relative flex flex-col">
                  {candidatesSearch.trim().length < 2 && poolCandidates.length === 0 ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-[11px] font-medium text-slate-400">Typ minimaal 2 letters om te zoeken in de talentpool...</span>
                    </div>
                  ) : poolCandidates.length === 0 && !isSearchingBackend ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-[11px] text-slate-400">
                      Geen kandidaten gevonden.
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto max-h-64 p-2 space-y-1">
                      {poolCandidates.map(c => (
                        <label key={c.id} className="flex items-center gap-3 p-3 hover:bg-white rounded-lg cursor-pointer transition-colors border border-transparent hover:border-slate-100 hover:shadow-sm">
                          <input
                            type="checkbox"
                            checked={selectedPoolIds.includes(c.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedPoolIds(prev => [...prev, c.id]);
                              else setSelectedPoolIds(prev => prev.filter(id => id !== c.id));
                            }}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 ml-1"
                          />
                          <Avatar name={c.name} photo={c.photo_url || c.avatar_url} />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-sm font-bold text-slate-800 truncate">{c.name}</span>
                            <div className="flex items-center gap-2 mt-0.5 min-w-0">
                              {c.candidate_number && (
                                <span className="text-[10px] font-black tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                                  #{c.candidate_number}
                                </span>
                              )}
                              <span className="text-xs font-medium text-slate-500 truncate">{c.job_title || c.role || c.email}</span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Box 3: Select Status */}
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                <label className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-3 block">3. Kies Pipeline Status</label>
                <div className="relative">
                  <select
                    value={targetAddStageId}
                    onChange={(e) => setTargetAddStageId(e.target.value)}
                    className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#0073e6]/20 focus:border-[#0073e6] outline-none appearance-none px-4 py-3 transition-colors"
                  >
                    {DEFAULT_KANBAN_STAGES.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-4 sm:px-6 border-t border-slate-100 bg-white shrink-0">
              <button
                onClick={handleAddFromPool}
                disabled={!targetAddVacancyId || selectedPoolIds.length === 0}
                className={`w-full py-4 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-sm ${!targetAddVacancyId || selectedPoolIds.length === 0
                    ? 'bg-slate-400 text-white cursor-not-allowed opacity-90'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md'
                  }`}
              >
                + {selectedPoolIds.length} KANDIDATEN TOEVOEGEN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {notification && (
        <div className={`fixed bottom-6 right-6 z-[999] px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-slideInRight ${notification.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
          <span className="text-xs font-bold tracking-wide">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="p-1 hover:bg-black/10 rounded-full transition-colors ml-4">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GlobalShortlistTable;
